import { StationIndicators } from '@app/components/stations/StationIndicators';
import { useStationFavorites } from '@app/core/stations/hooks/useStationFavorites';
import { Station } from '@app/core/types/stations';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import {
  Box,
  ChipProps,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { StationAvatar } from '../StationAvatar';
import { RecentStationListItem } from './RecentStationListItem';
import {
  favoritesSectionMessage,
  myStationsSectionMessage,
  recentSectionMessage,
  sharedGroupSectionMessage,
  sharedStationsSectionMessage,
} from './stations-selector-messages';
import { StationsSelectorSection } from './StationsSelectorSection';
import { useSortedFilteredStations } from './useSelectorStations';

interface StationsNavigatorPopoverProps {
  readonly open: boolean;
  readonly anchorEl: HTMLElement | null;
  readonly onClose: () => void;
  readonly stations: readonly Station[];
  readonly activeId: Station['id'];
  readonly onSelectStation: (station: Station) => void;
  readonly onRemoveRecentStation: (stationId: string) => void;
  readonly recentStationIds: readonly string[];
  readonly size?: ChipProps['size'];
}

export function StationsNavigatorPopover(props: StationsNavigatorPopoverProps) {
  const {
    open,
    anchorEl,
    onClose,
    stations,
    activeId,
    onSelectStation,
    onRemoveRecentStation,
    recentStationIds,
    size = 'medium',
  } = props;
  const { $t } = useIntl();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState('');
  const isSmall = size === 'small';
  const { favoriteIds: favoriteStationIds } = useStationFavorites();

  useEffect(() => {
    if (open) {
      setQuery('');
    }
  }, [open]);

  const {
    filteredStations,
    recentStations,
    favoriteStations,
    ownStations,
    individuallySharedStations,
    sharedGroupSections,
  } = useSortedFilteredStations({
    stations,
    recentStationIds,
    favoriteStationIds,
    query,
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }

    if (e.key === 'Enter' && !!filteredStations.length) {
      onSelectStation(filteredStations[0]);
    }
  };

  const showKeyboardIconForStation = (station: Station, globalIndex: number) =>
    globalIndex === 0 && query.trim() !== '' && filteredStations[0]?.id === station.id;

  const renderStationItem = (station: Station, globalIndex: number) => (
    <ListItemButton
      key={station.id}
      onClick={() => onSelectStation(station)}
      selected={station.id === activeId}
      dense={isSmall}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <ListItemAvatar sx={{ minWidth: isSmall ? 24 : 32, mr: 1 }}>
          <StationAvatar stationId={station.id} size={isSmall ? 16 : 20} />
        </ListItemAvatar>
        <ListItemText
          primary={
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              <Box component="span">{station.name}</Box>
              <StationIndicators
                station={station}
                variant="icon"
                size={isSmall ? 'small' : 'medium'}
              />
            </Box>
          }
          primaryTypographyProps={{
            variant: isSmall ? 'body2' : 'body1',
          }}
        />
      </Box>
      {showKeyboardIconForStation(station, globalIndex) && (
        <Tooltip
          title={$t({
            id: 'ui.stationsSelector.enterToGo',
            defaultMessage: 'Enter para ir',
          })}
        >
          <KeyboardReturnIcon
            sx={{
              fontSize: isSmall ? 16 : 18,
              color: 'text.secondary',
              ml: 'auto',
            }}
          />
        </Tooltip>
      )}
    </ListItemButton>
  );

  let globalIndex = 0;

  const renderStandardSection = (
    key: string,
    title: typeof favoritesSectionMessage,
    sectionStations: readonly Station[],
    titleValues?: Record<string, string>,
  ) => {
    if (sectionStations.length === 0) {
      return null;
    }

    const startIndex = globalIndex;
    globalIndex += sectionStations.length;
    const hasPreviousSection = startIndex > 0;

    return (
      <StationsSelectorSection
        key={key}
        title={title}
        titleValues={titleValues}
        stations={sectionStations}
        isSmall={isSmall}
        hasPreviousSection={hasPreviousSection}
        startIndex={startIndex}
        renderStation={renderStationItem}
      />
    );
  };

  const renderRecentSection = () => {
    if (recentStations.length === 0) {
      return null;
    }

    const startIndex = globalIndex;
    globalIndex += recentStations.length;

    return (
      <Fragment key="recent">
        <ListSubheader sx={isSmall ? { lineHeight: 28, typography: 'caption' } : undefined}>
          {$t(recentSectionMessage)}
        </ListSubheader>
        {recentStations.map((station, index) => (
          <RecentStationListItem
            key={station.id}
            station={station}
            activeId={activeId}
            isSmall={isSmall}
            showKeyboardIcon={showKeyboardIconForStation(station, startIndex + index)}
            onSelectStation={onSelectStation}
            onRemoveRecentStation={onRemoveRecentStation}
          />
        ))}
      </Fragment>
    );
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      disableRestoreFocus
      TransitionProps={{
        onEntered: () => {
          inputRef.current?.focus();
        },
      }}
      PaperProps={{
        sx: {
          mt: 1,
          p: isSmall ? 1 : 1.5,
          width: isSmall ? 300 : 370,
          maxWidth: 'calc(100vw - 32px)',
        },
      }}
    >
      <TextField
        fullWidth
        size="small"
        placeholder={$t({
          id: 'ui.stationsSelector.searchPlaceholder',
          defaultMessage: 'Buscar emisora',
        })}
        inputRef={inputRef}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
      />

      <Box
        sx={{
          mt: 1,
          maxHeight: 'calc(50vh)',
          overflow: 'auto',
        }}
      >
        {!filteredStations.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: isSmall ? 0.75 : 1 }}>
            {$t({
              id: 'common.noResults',
              defaultMessage: 'Sin resultados',
            })}
          </Typography>
        ) : (
          <List disablePadding>
            {renderRecentSection()}
            {renderStandardSection('favorites', favoritesSectionMessage, favoriteStations)}
            {renderStandardSection('own', myStationsSectionMessage, ownStations)}
            {renderStandardSection(
              'shared',
              sharedStationsSectionMessage,
              individuallySharedStations,
            )}
            {sharedGroupSections.map((section) =>
              renderStandardSection(section.group.id, sharedGroupSectionMessage, section.stations, {
                name: section.group.name,
              }),
            )}
          </List>
        )}
      </Box>
    </Popover>
  );
}
